import { Group, NumberInput, Paper, Stack, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLandAufschluesselung } from '@/api/useFinanzierung';
import { usePlan, useUpdatePlan } from '@/api/usePlaene';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import type { LandBlock, LandZeile, UUID } from '@/api/types';
import { describeApiError, formatEuro } from './utils';

interface Props {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
}

/** Anteil der Merkmals-Kinder, ab dem die Schwerpunktkita-Pauschale greift. */
const SCHWERPUNKT_SCHWELLE = 0.22;

/**
 * Die Landesfoerderung aufgeschluesselt — nach dem Vorbild der
 * Excel-Vorlage "Landesfoerderung_Muster.pdf": je Pauschale ein Kasten mit
 * Betreuungsart, Satz, Anzahl und Jahresbetrag.
 *
 * Die Kinderzahlen werden **nicht eingetippt**. Sie kommen aus der Belegung
 * zum Stichtag 1. Maerz, bei der Integrationspauschale aus der Kinderliste
 * unter "Belegung → Inklusion". Eine zweite Pflegestelle koennte der ersten
 * widersprechen.
 *
 * Eine Ausnahme: die Schwerpunktkita-Pauschale. Ihre Merkmals-Kinder stehen
 * nirgends sonst — sie werden hier eingegeben, in dem Kasten, der auch zeigt,
 * was daraus folgt. Frueher lag das Feld im Belegung-Reiter, weit weg von
 * seiner Wirkung.
 */
export function LandAufschluesselungBlock({ planId, disabled, onMutated }: Props) {
  const { t, i18n } = useTranslation(['finanzierung', 'belegung', 'common']);
  const daten = useLandAufschluesselung(planId);

  if (daten.isLoading) return <LoadingState height={160} />;
  if (daten.isError) return <ErrorState error={daten.error} />;
  if (!daten.data || daten.data.bloecke.length === 0) return null;

  return (
    <Stack data-testid="land-aufschluesselung">
      <Stack gap={2}>
        <Title order={4}>{t('finanzierung:land.titel')}</Title>
        <Text size="xs" c="dimmed">
          {t('finanzierung:land.stichtagHinweis')}
        </Text>
      </Stack>

      {gruppiere(daten.data.bloecke).map((eintrag) => (
        <PauschaleKasten
          key={eintrag.schluessel}
          gruppe={eintrag.gruppe}
          bloecke={eintrag.bloecke}
          locale={i18n.language}
          planId={planId}
          disabled={disabled}
          onMutated={onMutated}
          gesamtKinder={daten.data.gesamt_kinder_stichtag}
        />
      ))}

      <Group
        justify="space-between"
        pt="xs"
        style={{ borderTop: '1px solid var(--mantine-color-gray-4)' }}
      >
        <Text fw={700}>{t('finanzierung:land.gesamt')}</Text>
        <Text fw={700} data-testid="land-gesamtsumme">
          {formatEuro(Number(daten.data.summe_jahr), i18n.language)}
        </Text>
      </Group>
    </Stack>
  );
}

/**
 * Bloecke zu Kaesten zusammenfassen.
 *
 * Die Integrationsfoerderung besteht aus zwei Toepfen — Pauschale nach
 * §32 (5) und Zuschuss aus der Rahmenvereinbarung Teilhabe. Sie stehen
 * unter einer Ueberschrift, weil sie fachlich eine Foerderung sind; jeder
 * Topf behaelt seine eigene Tabelle und Summe. Bloecke ohne ``gruppe``
 * bilden je einen eigenen Kasten.
 *
 * Die Reihenfolge des Servers bleibt erhalten: Der Kasten entsteht dort,
 * wo sein erster Block steht.
 */
function gruppiere(
  bloecke: LandBlock[],
): { schluessel: string; gruppe: string | null; bloecke: LandBlock[] }[] {
  const out: { schluessel: string; gruppe: string | null; bloecke: LandBlock[] }[] = [];
  const nachGruppe = new Map<string, number>();
  for (const block of bloecke) {
    const gruppe = block.gruppe ?? null;
    if (gruppe === null) {
      out.push({ schluessel: block.art, gruppe: null, bloecke: [block] });
      continue;
    }
    const vorhanden = nachGruppe.get(gruppe);
    if (vorhanden === undefined) {
      nachGruppe.set(gruppe, out.length);
      out.push({ schluessel: `gruppe-${gruppe}`, gruppe, bloecke: [block] });
    } else {
      out[vorhanden].bloecke.push(block);
    }
  }
  return out;
}

interface KastenProps {
  gruppe: string | null;
  bloecke: LandBlock[];
  locale: string;
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
  gesamtKinder: number;
}

/**
 * Ein grauer Themenblock. Enthaelt entweder eine Pauschale oder — bei der
 * Integrationsfoerderung — zwei Toepfe unter einer Ueberschrift, jeder mit
 * eigener Tabelle und eigener Summe.
 */
function PauschaleKasten({
  gruppe,
  bloecke,
  locale,
  planId,
  disabled,
  onMutated,
  gesamtKinder,
}: KastenProps) {
  const { t, i18n } = useTranslation(['finanzierung', 'common']);
  const einzeln = bloecke.length === 1 && gruppe === null;

  // Ueberschrift des Kastens: bei einer Gruppe deren Name, sonst der der
  // einzigen Pauschale darin.
  const ueberschrift = gruppe
    ? t(`finanzierung:land.gruppe.${gruppe}`)
    : t(`finanzierung:pauschale.${bloecke[0].art}`);
  const rechtsgrundlage = einzeln ? bloecke[0].rechtsgrundlage : null;

  return (
    <Paper
      withBorder
      p="md"
      bg="var(--mantine-color-gray-0)"
      style={{ borderColor: 'var(--mantine-color-gray-3)' }}
      data-testid={gruppe ? `land-gruppe-${gruppe}` : `land-kasten-${bloecke[0].art}`}
    >
      <Group gap="xs" align="baseline" mb="sm">
        <Text size="xs" tt="uppercase" fw={700} c="dimmed" style={{ letterSpacing: 1 }}>
          {ueberschrift}
        </Text>
        {rechtsgrundlage ? (
          <Text size="xs" c="dimmed">
            {rechtsgrundlage}
          </Text>
        ) : null}
      </Group>

      <Stack gap="sm">
        {bloecke.map((block) => {
          // Innerhalb einer Gruppe braucht jeder Topf seinen eigenen Namen —
          // "Integrationspauschale" statt der Gruppenueberschrift daruber.
          const blockKey = `finanzierung:land.block.${block.art}`;
          const blockName = i18n.exists(blockKey)
            ? t(blockKey)
            : t(`finanzierung:pauschale.${block.art}`);

          // Gestaffelte Pauschalen zeigen nur die belegten Baender.
          //
          // Die Grundpauschale fuehrt acht, die Integrationspauschale
          // zwoelf — gefuellt sind meist zwei oder drei. Die leeren Zeilen
          // sagen nichts: Die Anzahl kommt aus der Belegung, und ob dort
          // etwas fehlt, sieht man im Belegung-Reiter, nicht hier.
          //
          // Bloecke mit genau einer Zeile behalten sie, auch bei Anzahl 0.
          // Dort IST die Null die Aussage — die Schwerpunktkita-Pauschale
          // unterhalb der 22-%-Schwelle, die Kleinkita-Pauschale ab 25
          // Kindern. Wuerde man sie wegfiltern, stuende der Kasten leer da
          // und man wuesste nicht, woran es liegt.
          const zeilen =
            block.zeilen.length === 1
              ? block.zeilen
              : block.zeilen.filter((z) => Number(z.anzahl) > 0);
          return (
            <Paper withBorder p="md" key={block.art} data-testid={`land-block-${block.art}`}>
              {gruppe ? (
                // Title order={5} wie bei den Karten in den Themenbloecken
                // unter Belegung ("Gruppen") und Personalbedarf
                // ("Mindestpersonalbedarf"). Ein blosser Fettdruck waere
                // schwaecher als die Tabellenkoepfe darunter — die
                // Gliederung stuende dann auf dem Kopf.
                <Group gap="xs" align="baseline" mb="xs">
                  <Title order={5}>{blockName}</Title>
                  {block.rechtsgrundlage ? (
                    <Text size="xs" c="dimmed">
                      {block.rechtsgrundlage}
                    </Text>
                  ) : null}
                </Group>
              ) : null}

              {block.art === 'schwerpunktkita' ? (
                <SchwerpunktEingabe
                  planId={planId}
                  disabled={disabled}
                  onMutated={onMutated}
                  gesamtKinder={gesamtKinder}
                />
              ) : null}

              <Table withColumnBorders={false} verticalSpacing={2} horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      {/* Jede Foerderung bemisst sich nach etwas anderem:
                          die Pauschalen nach Betreuungsart, der
                          Teilhabe-Zuschuss nach Stunden, die
                          Fachkraefteoffensive nach Ausbildungsmonaten. Die
                          Spaltenkoepfe sagen, worin gerechnet wird. */}
                      {t(
                        block.art === 'teilhabe'
                          ? 'finanzierung:land.spalte.bandStunden'
                          : block.art === 'piva'
                            ? 'finanzierung:land.spalte.bandAusbildung'
                            : 'finanzierung:land.spalte.band',
                      )}
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right', width: 120 }}>
                      {t(
                        block.art === 'piva'
                          ? 'finanzierung:land.spalte.satzMonat'
                          : 'finanzierung:land.spalte.satz',
                      )}
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right', width: 90 }}>
                      {t(
                        block.art === 'piva'
                          ? 'finanzierung:land.spalte.monate'
                          : 'finanzierung:land.spalte.anzahl',
                      )}
                    </Table.Th>
                    <Table.Th style={{ textAlign: 'right', width: 130 }}>
                      {t('finanzierung:land.spalte.betrag')}
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {zeilen.length === 0 ? (
                    // Ohne diese Zeile stuende die Tabelle leer da und man
                    // wuesste nicht, ob etwas fehlt oder nichts zutrifft.
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text size="sm" c="dimmed">
                          {t('finanzierung:land.keineZeilen')}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {zeilen.map((zeile, i) => (
                    <Zeile
                      key={`${zeile.kategorie}-${zeile.stundenklasse}-${i}`}
                      zeile={zeile}
                      locale={locale}
                      art={block.art}
                    />
                  ))}
                  <Table.Tr>
                    <Table.Td colSpan={3} style={{ fontWeight: 600 }}>
                      {t('finanzierung:land.summe')}
                    </Table.Td>
                    <Table.Td
                      style={{ textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}
                      data-testid={`land-summe-${block.art}`}
                    >
                      {formatEuro(Number(block.summe_jahr), locale)}
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}

/**
 * Anzahl der Merkmals-Kinder plus die Schwellenrechnung.
 *
 * Die Pauschale gibt es nur, wenn der Anteil an allen Kindern am Stichtag
 * mindestens 22 % betraegt. Ohne diese Anzeige saehe man bei 0 EUR nicht,
 * ob die Zahl fehlt oder die Schwelle gerissen ist.
 */
function SchwerpunktEingabe({
  planId,
  disabled,
  onMutated,
  gesamtKinder,
}: {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
  gesamtKinder: number;
}) {
  const { t } = useTranslation(['belegung', 'errors']);
  const plan = usePlan(planId);
  const updatePlan = useUpdatePlan();
  const [wert, setWert] = useState<number | ''>('');

  useEffect(() => {
    const gespeichert = plan.data?.schwerpunkt_kinder_anzahl;
    setWert(gespeichert ?? '');
  }, [plan.data?.schwerpunkt_kinder_anzahl]);

  const speichern = (roh: number | '') => {
    const value =
      roh === '' || Number.isNaN(roh) ? null : Math.max(0, Math.round(Number(roh)));
    if (plan.data && plan.data.schwerpunkt_kinder_anzahl === value) {
      // Unveraendert — trotzdem neu rechnen lassen, falls der Snapshot aus
      // einer frueheren Engine-Version stammt.
      onMutated();
      return;
    }
    void updatePlan
      .mutateAsync({ id: planId, patch: { schwerpunkt_kinder_anzahl: value } })
      .then(() => onMutated())
      .catch((err) => {
        notifications.show({
          color: 'red',
          title: t('errors:generic'),
          message: describeApiError(err) || t('errors:generic'),
        });
      });
  };

  const merkmal = typeof wert === 'number' ? wert : 0;
  const anteil = gesamtKinder > 0 ? merkmal / gesamtKinder : 0;
  const ueberSchwelle = anteil >= SCHWERPUNKT_SCHWELLE;
  const fmt = (n: number, d = 1) =>
    n.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });

  return (
    <Stack gap="xs" mb="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text size="xs" c="dimmed">
            {t('belegung:section.schwerpunkt.merkmal1')}
            <br />
            {t('belegung:section.schwerpunkt.merkmal2')}
            <br />
            {t('belegung:section.schwerpunkt.merkmal3')}
          </Text>
        </Stack>
        <Stack gap={2} style={{ minWidth: 200 }}>
          <NumberInput
            label={t('belegung:section.schwerpunkt.inputLabel')}
            description={t('belegung:section.schwerpunkt.inputHint')}
            placeholder="0"
            min={0}
            max={9999}
            step={1}
            allowDecimal={false}
            value={wert}
            onChange={(v) => setWert(typeof v === 'number' ? v : '')}
            onBlur={() => speichern(wert)}
            disabled={disabled || updatePlan.isPending}
            size="xs"
            aria-label={t('belegung:section.schwerpunkt.inputLabel')}
          />
        </Stack>
      </Group>

      {merkmal > 0 ? (
        <Text size="sm" data-testid="schwerpunkt-anteil">
          {t('belegung:section.schwerpunkt.anteil')}
          {': '}
          <Text component="span" fw={600}>
            {merkmal} / {gesamtKinder} ={' '}
          </Text>
          <Text component="span" fw={600} c={ueberSchwelle ? 'teal' : 'red'}>
            {fmt(anteil * 100)} %
          </Text>
          <Text component="span" c="dimmed" size="xs">
            {' '}
            (
            {ueberSchwelle
              ? t('belegung:section.schwerpunkt.schwelleOk')
              : t('belegung:section.schwerpunkt.schwelleNo')}
            )
          </Text>
        </Text>
      ) : null}
    </Stack>
  );
}

function Zeile({
  zeile,
  locale,
  art,
}: {
  zeile: LandZeile;
  locale: string;
  art: string;
}) {
  const { t, i18n } = useTranslation('finanzierung');
  const anzahl = Number(zeile.anzahl);
  // Leere Baender stehen bewusst da, aber zurueckhaltend: Der Blick soll an
  // den befuellten haengenbleiben.
  const leer = anzahl === 0;

  // Bei der Fachkraefteoffensive traegt die Kategorie das Ausbildungsjahr,
  // nicht die Altersgruppe — sie ist der Staffel-Schluessel der jeweiligen
  // Foerderung.
  const kategorieLabel = !zeile.kategorie
    ? null
    : art === 'piva'
      ? t('finanzierung:land.ausbildungsjahr', { jahr: zeile.kategorie })
      : t(`finanzierung:land.kategorie.${zeile.kategorie}`);
  const stundenKey = zeile.stundenklasse
    ? `finanzierung:land.stundenklasse.${zeile.stundenklasse}`
    : null;
  const stundenLabel = stundenKey && i18n.exists(stundenKey) ? t(stundenKey) : null;
  // Der Name steht in Klammern dahinter: Bei mehreren Auszubildenden sehen
  // die Zeilen sonst gleich aus, und man weiss nicht, welche zu wem gehoert.
  const bandOhneNamen = [kategorieLabel, stundenLabel].filter(Boolean).join(' · ');
  const band = zeile.bezeichnung
    ? `${bandOhneNamen} (${zeile.bezeichnung})`
    : bandOhneNamen;

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm" c={leer ? 'dimmed' : undefined}>
          {band ||
            (art === 'teilhabe'
              ? t('finanzierung:land.stundenZeile')
              : t('finanzierung:land.pauschalbetrag'))}
        </Text>
      </Table.Td>
      <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Text size="sm" c="dimmed">
          {formatEuro(Number(zeile.satz_jaehrlich), locale)}
        </Text>
      </Table.Td>
      <Table.Td style={{ textAlign: 'right' }}>
        <Text size="sm" c={leer ? 'dimmed' : undefined}>
          {anzahl.toLocaleString(locale, { maximumFractionDigits: 2 })}
        </Text>
      </Table.Td>
      <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <Text size="sm" c={leer ? 'dimmed' : undefined}>
          {formatEuro(Number(zeile.betrag_jahr), locale)}
        </Text>
      </Table.Td>
    </Table.Tr>
  );
}
